import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { tiktokService, WizardGoal } from '@/services/tiktok.service';
import {
  Music2, MousePointerClick, Eye, Zap, AppWindow, Globe2, MessageCircle,
  ChevronRight, ChevronLeft, Check, CircleDollarSign, Calendar,
  Video, Sparkles, ArrowUpRight, Wand2, Images,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { MediaLibraryPicker } from '@/components/MediaLibraryPicker';
import { useTiktokAccountCurrency } from '@/hooks/useTiktokAccountCurrency';
import { MarketingApp, marketingAppsService } from '@/services/marketing-apps.service';

// ── User-facing goals → labels + icons. Backend handles the TikTok mapping. ──
const GOALS: Array<{
  id: WizardGoal;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  whoFor: string;
}> = [
  { id: 'video_views',     title: 'Get more video views',  subtitle: 'TikTok shows your video to people most likely to watch.', whoFor: 'Brand storytelling, content creators',                  icon: <Eye size={18} /> },
  { id: 'traffic',         title: 'Drive traffic to a website', subtitle: 'Send people from TikTok to your landing page.',         whoFor: 'E-commerce, product pages, lead-magnet pages',          icon: <MousePointerClick size={18} /> },
  { id: 'lead_generation', title: 'Collect leads',           subtitle: 'Capture contact details with an instant lead form.',     whoFor: 'B2B, services, education, real estate',                 icon: <MessageCircle size={18} /> },
  { id: 'conversions',     title: 'Get conversions',         subtitle: 'Optimise for purchases or sign-ups via your TikTok pixel.', whoFor: 'Online stores with a TikTok pixel installed',         icon: <Zap size={18} /> },
  { id: 'app_installs',    title: 'Promote an app',          subtitle: 'Send people to your iOS or Android store listing.',      whoFor: 'Registered apps · clicks only, no install tracking',              icon: <AppWindow size={18} /> },
  { id: 'awareness',       title: 'Build awareness',         subtitle: 'Reach as many unique people as possible.',                whoFor: 'New brands, launches, sponsorships',                    icon: <Globe2 size={18} /> },
];

const CTA_OPTIONS = [
  'LEARN_MORE', 'SHOP_NOW', 'SIGN_UP', 'DOWNLOAD_NOW', 'GET_QUOTE',
  'CONTACT_US', 'BOOK_NOW', 'INSTALL_NOW', 'WATCH_NOW', 'VIEW_PROFILE',
];

type Step = 1 | 2 | 3 | 4;

export default function TiktokWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const registryAppUid = params.get('appUid');
  const [registryApp, setRegistryApp] = useState<MarketingApp | null>(null);
  const [registryAccount, setRegistryAccount] = useState<{ uid: string; name: string; currency: string } | null>(null);
  const [registryError, setRegistryError] = useState('');
  const [registryLoading, setRegistryLoading] = useState(!!registryAppUid);
  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);

  // Step 1
  const [goal, setGoal] = useState<WizardGoal | ''>('');

  // Step 2
  const [name, setName] = useState('');
  const [dailyBudget, setDailyBudget] = useState<string>('50');
  const [endsOnDate, setEndsOnDate] = useState(false);
  const [endDate, setEndDate] = useState('');
  const launchActive = false;
  const { currency: defaultCurrency } = useTiktokAccountCurrency();
  const currency = registryAccount?.currency ?? defaultCurrency;
  const [apps, setApps] = useState<Awaited<ReturnType<typeof tiktokService.listApps>>>([]);
  const [forms, setForms] = useState<Awaited<ReturnType<typeof tiktokService.listLeadForms>>>([]);
  const [pixels, setPixels] = useState<Awaited<ReturnType<typeof tiktokService.listPixelOptions>>>([]);
  const [countries, setCountries] = useState<Array<{ id: string; name: string; code?: string }>>([]);
  const [appId, setAppId] = useState('');
  const [pageId, setPageId] = useState('');
  const [pixelId, setPixelId] = useState('');
  const [optimizationEvent, setOptimizationEvent] = useState('');
  const [assetError, setAssetError] = useState('');
  const [assetLoading, setAssetLoading] = useState(false);
  // TikTok requires geo targeting on every ad group — default to UAE.
  const [locationIds, setLocationIds] = useState<string[]>(['290557']);

  const toggleLocation = (id: string) =>
    setLocationIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  // Step 3
  const [identityId, setIdentityId] = useState('');
  const [identities, setIdentities] = useState<Array<{ identityId: string; identityType: string; displayName?: string }>>([]);
  const [videoId, setVideoId] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [adText, setAdText] = useState('');
  const [callToAction, setCallToAction] = useState('LEARN_MORE');
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [enhancing, setEnhancing] = useState(false);
  useEffect(() => {
    let current = true;
    setRegistryApp(null); setRegistryAccount(null); setRegistryError(''); setRegistryLoading(!!registryAppUid);
    if (!registryAppUid) return;
    setStep(1); setGoal(''); setVideoId(''); setAppId('');
    Promise.all([marketingAppsService.get(registryAppUid), tiktokService.getDefault()]).then(([app, account]) => {
      if (!current) return;
      if (!app.readiness.tiktok.detailsReady || !app.tiktokAppId) throw new Error('Add the TikTok app ID and store URL to an active app first.');
      if (!account?.isActive || !account.isDefault || account.companyUid !== app.companyUid) throw new Error('Choose an active TikTok advertiser before promoting this app.');
      setRegistryApp(app); setRegistryAccount({ uid: account.accountUid, name: account.advertiserName || account.advertiserId, currency: account.currency || '' });
      setAppId(app.tiktokAppId); setName(`${app.name} · App promotion`); setGoal('app_installs'); setCallToAction('INSTALL_NOW');
    }).catch(error => { if (current) setRegistryError(error?.response ? 'Could not load your saved app or TikTok advertiser.' : error.message); })
      .finally(() => { if (current) setRegistryLoading(false); });
    return () => { current = false; };
  }, [registryAppUid]);

  // Map the user-facing wizard goal to a TikTok objective code so the AI
  // gets the right tone hint. Backend handles unknown values gracefully.
  const goalToObjective: Record<WizardGoal, string> = {
    video_views:     'VIDEO_VIEWS',
    traffic:         'TRAFFIC',
    lead_generation: 'LEAD_GENERATION',
    conversions:     'CONVERSIONS',
    app_installs:    'APP_PROMOTION',
    awareness:       'REACH',
  };

  const enhance = async () => {
    setEnhancing(true);
    try {
      const ident = identities.find((i) => i.identityId === identityId);
      // Enhance uses your default model from /settings/ai — generation flows
      // still let the user pick a model.
      const r = await tiktokService.enhanceAdCopy({
        adFormat: 'SINGLE_VIDEO',
        objective: goal ? goalToObjective[goal] : undefined,
        draftAdName: name || undefined,
        draftAdText: adText || undefined,
        draftCallToAction: callToAction || undefined,
        brandName: ident?.displayName,
        landingPageUrl: landingPageUrl || undefined,
      });
      // The wizard doesn't have a separate "ad name" field at step 3 — only
      // adText + CTA. Apply just those two; campaign name stays untouched.
      if (r.adText) setAdText(r.adText);
      if (r.callToAction) setCallToAction(r.callToAction);
      toast.success('Enhanced with AI');
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setEnhancing(false); }
  };

  useEffect(() => {
    let current = true;
    tiktokService.listIdentities()
      .then((rows) => {
        if (!current) return;
        const compatible = rows.filter(row => ['TT_USER', 'BC_AUTH_TT'].includes(row.identityType));
        setIdentities(compatible);
        if (compatible[0]) setIdentityId(compatible[0].identityId);
      })
      .catch(() => undefined);
    return () => { current = false; };
  }, [registryAccount?.uid]);

  useEffect(() => {
    let current = true;
    setAssetError(''); setAssetLoading(!!goal); setCountries([]); setApps([]); setForms([]); setPixels([]);
    if (!goal) return;
    Promise.all([
      tiktokService.listRegions({ placement: 'PLACEMENT_TIKTOK', objectiveType: goalToObjective[goal] }),
      goal === 'app_installs' ? tiktokService.listApps(registryAccount?.uid) : Promise.resolve([]),
      goal === 'lead_generation' ? tiktokService.listLeadForms() : Promise.resolve([]),
      goal === 'conversions' ? tiktokService.listPixelOptions() : Promise.resolve([]),
    ]).then(([regions, appRows, formRows, pixelRows]) => {
      if (!current) return;
      setCountries(regions.map(row => ({ id: row.locationId, name: row.name, code: row.regionCode })));
      setLocationIds(previous => previous.filter(id => regions.some(row => row.locationId === id)));
      setApps(appRows); setForms(formRows); setPixels(pixelRows);
    }).catch(() => { if (current) setAssetError('Campaign options could not be loaded. Reload the page to retry.'); })
      .finally(() => { if (current) setAssetLoading(false); });
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal, registryAccount?.uid]);

  const goalMeta = useMemo(() => GOALS.find((g) => g.id === goal), [goal]);
  const registeredApp = registryApp ? apps.find(app => app.appId === registryApp.tiktokAppId) : undefined;
  const registryMatches = !!(registeredApp && registryApp && registeredApp.platform === (registryApp.platform === 'ios' ? 'IOS' : 'ANDROID')
    && (!registeredApp.packageName || [registryApp.androidPackageName, registryApp.iosBundleId, registryApp.googleAppIdentifier].includes(registeredApp.packageName)));
  const registryReady = !registryAppUid || (!registryLoading && !assetLoading && !assetError && !registryError && !!registryAccount && registryMatches);

  const canAdvance: Record<Step, boolean> = {
    1: !!goal && registryReady,
    2: !assetError && countries.length > 0 && !!name.trim() && parseFloat(dailyBudget) > 0 && (!endsOnDate || !!endDate) && locationIds.length > 0,
    // TikTok rejects TRAFFIC (CLICK) ads without a landing page — require it up front.
    3: !assetError && !!identityId && !!videoId && !!adText.trim() && (goal === 'app_installs' ? apps.some(app => app.appId === appId && app.platform !== 'UNKNOWN') : goal === 'lead_generation' ? forms.some(form => form.pageId === pageId) : /^https?:\/\//.test(landingPageUrl.trim())) && (goal !== 'conversions' || !!pixels.find(pixel => pixel.pixelId === pixelId)?.events.some(event => event.eventType === optimizationEvent)),
    4: true,
  };

  const next = () => { if (canAdvance[step] && step < 4) setStep((s) => (s + 1) as Step); };
  const back = () => { if (step > 1) setStep((s) => (s - 1) as Step); };

  const onUploadVideo = async () => {
    if (!videoUrlInput.trim()) {
      toast.error('Paste a video URL first');
      return;
    }
    setUploadingVideo(true);
    try {
      const r = await tiktokService.uploadVideoByUrl({ videoUrl: videoUrlInput.trim() });
      setVideoId(r.videoId);
      setVideoUrl(r.videoUrl ?? '');
      toast.success('Video uploaded');
    } catch (e: unknown) {
      // The axios interceptor already toasts server errors — only toast when
      // there was no response (network failure etc.) to avoid duplicates.
      if (!(e as { response?: unknown })?.response) toast.error('Upload failed');
    } finally { setUploadingVideo(false); }
  };

  const onLaunch = async () => {
    if (!goal) return;
    setSubmitting(true);
    try {
      if (!registryReady) { toast.error('Resolve the app registration details before saving.'); return; }
      const result = await tiktokService.wizardLaunch({
        registryAppUid: registryAppUid || undefined,
        expectedAccountUid: registryAccount?.uid,
        goal,
        name: name.trim(),
        dailyBudget: parseFloat(dailyBudget),
        endTime: endsOnDate && endDate ? `${endDate} 23:59:59` : undefined,
        videoId,
        identityId,
        identityType: identities.find((i) => i.identityId === identityId)?.identityType,
        adText: adText.trim(),
        callToAction,
        landingPageUrl: goal === 'app_installs' || goal === 'lead_generation' ? undefined : landingPageUrl.trim() || undefined,
        locationIds,
        launchActive,
        appId: goal === 'app_installs' ? appId : undefined,
        pageId: goal === 'lead_generation' ? pageId : undefined,
        pixelId: goal === 'conversions' ? pixelId : undefined,
        optimizationEvent: goal === 'conversions' ? optimizationEvent : undefined,
      });
      toast.success(launchActive ? 'Campaign launched! 🚀' : 'Campaign created (paused)');
      navigate(`/tiktok/campaigns?highlight=${result.campaignId}`);
    } catch (e: unknown) {
      // The axios interceptor already toasts server errors — only toast when
      // there was no response (network failure etc.) to avoid duplicates.
      if (!(e as { response?: unknown })?.response) toast.error('Launch failed');
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Topbar
        title="New TikTok campaign"
        breadcrumb="TikTok ads"
        description="One guided flow — we'll set up the campaign, ad group, and ad together."
        right={
          <Link to="/tiktok/campaigns" className="text-xs text-text-muted hover:text-primary inline-flex items-center gap-1">
            Use advanced flow <ArrowUpRight size={12} />
          </Link>
        }
      />

      <div className="px-7 py-7 max-w-3xl mx-auto">
        {registryAppUid && <Card className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="font-semibold text-text-primary">{registryApp?.name || 'Saved app'}</h2><Link to={`/marketing/apps/${encodeURIComponent(registryAppUid)}`} className="text-xs text-primary">App details</Link></div>
          {registryLoading ? <p className="mt-2 text-sm text-text-muted">Checking app registration…</p> : registryError ? <p role="alert" className="mt-2 text-sm text-danger">{registryError}</p> : <div className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
            <div><p className="text-text-muted">Selected advertiser</p><p className="mt-1 text-text-primary">{registryAccount?.name}</p></div>
            <div><p className="text-text-muted">Registered in TikTok</p><p className="mt-1 text-text-primary">{assetLoading ? 'Checking registration…' : assetError ? 'Could not verify' : registeredApp ? registryMatches ? `Yes · ${registeredApp.platform}` : 'Found · platform or identifier mismatch' : 'Not found on this advertiser'}</p></div>
            <div><p className="text-text-muted">Tracking source</p><p className="mt-1 text-text-primary">Not configured</p></div><div><p className="text-text-muted">Available optimization</p><p className="mt-1 text-text-primary">{registryMatches ? 'Clicks to the app store' : 'Complete app registration first'}</p></div>
          </div>}
          {assetError && <p role="alert" className="mt-2 text-xs text-danger">{assetError}</p>}
          <p className="mt-3 text-xs text-text-muted">Install optimization and in-app events require a tracking source. <Link to="/tiktok/account" className="text-primary">Change advertiser</Link></p>
        </Card>}
        {/* Stepper */}
        <div className="mb-6 flex items-center justify-between">
          {(['Goal', 'Budget', 'Creative', 'Review'] as const).map((label, i) => {
            const n = (i + 1) as Step;
            const active = step === n;
            const done = step > n;
            return (
              <div key={label} className="flex items-center">
                <div className={clsx(
                  'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition',
                  done ? 'bg-success text-white' :
                  active ? 'bg-primary text-on-primary' :
                  'bg-surface-alt text-text-muted',
                )}>
                  {done ? <Check size={14} /> : n}
                </div>
                <span className={clsx('ml-2 text-xs font-semibold', active ? 'text-text-primary' : 'text-text-muted')}>
                  {label}
                </span>
                {n < 4 && <div className="mx-3 h-px w-8 bg-border" />}
              </div>
            );
          })}
        </div>

        {/* Step 1 — Goal */}
        {step === 1 && (
          <Card>
            <h3 className="mb-1 text-base font-bold text-text-primary">What do you want from this campaign?</h3>
            <p className="mb-4 text-xs text-text-muted">Pick the outcome — we'll handle TikTok's optimisation settings for you.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {GOALS.filter(g => !registryAppUid || g.id === 'app_installs').map((g) => (
                <button
                  key={g.id}
                  onClick={() => setGoal(g.id)}
                  className={clsx(
                    'rounded-xl border p-4 text-left transition',
                    goal === g.id
                      ? 'border-primary bg-primary-soft'
                      : 'border-border bg-surface hover:border-primary/40 hover:bg-surface-alt',
                  )}
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span className={clsx(
                      'flex h-8 w-8 items-center justify-center rounded-lg',
                      goal === g.id ? 'bg-primary text-on-primary' : 'bg-primary-soft text-primary',
                    )}>{g.icon}</span>
                    <span className="text-sm font-bold text-text-primary">{g.title}</span>
                  </div>
                  <p className="text-xs text-text-muted">{g.subtitle}</p>
                  <p className="mt-2 text-2xs text-text-muted"><strong>For:</strong> {g.whoFor}</p>
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* Step 2 — Name + budget + schedule */}
        {step === 2 && (
          <Card>
            <h3 className="mb-1 text-base font-bold text-text-primary">Name + budget</h3>
            <p className="mb-4 text-xs text-text-muted">A daily budget controls spend; you can pause anytime.</p>

            <FormField label="Campaign name" required>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Spring sale launch" autoFocus maxLength={120} />
            </FormField>

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label="Daily budget (advertiser currency)" required>
                <div className="relative">
                  <CircleDollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <Input
                    type="number"
                    min="1"
                    step="any"
                    value={dailyBudget}
                    onChange={(e) => setDailyBudget(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <p className="mt-1 text-2xs text-text-muted">Budget is in {currency ?? 'the advertiser currency'}. TikTok checks the minimum for your account and goal.</p>
              </FormField>

              <FormField label="When does it end?">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEndsOnDate(false)}
                    className={clsx(
                      'flex-1 rounded-lg border px-3 py-2 text-xs font-semibold transition',
                      !endsOnDate ? 'border-primary bg-primary-soft text-primary' : 'border-border text-text-secondary',
                    )}
                  >
                    Run continuously
                  </button>
                  <button
                    onClick={() => setEndsOnDate(true)}
                    className={clsx(
                      'flex-1 rounded-lg border px-3 py-2 text-xs font-semibold transition',
                      endsOnDate ? 'border-primary bg-primary-soft text-primary' : 'border-border text-text-secondary',
                    )}
                  >
                    End on a date
                  </button>
                </div>
                {endsOnDate && (
                  <div className="mt-2 relative">
                    <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="pl-9" />
                  </div>
                )}
              </FormField>
            </div>

            <FormField label="Countries" required>
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 max-h-44 overflow-y-auto rounded-xl border border-border bg-surface-alt/40 p-2">
                {countries.map((c) => {
                  const active = locationIds.includes(c.id);
                  return (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => toggleLocation(c.id)}
                      className={clsx(
                        'rounded-lg border px-2 py-1.5 text-left text-xs transition-colors',
                        active
                          ? 'border-primary bg-primary-soft text-primary'
                          : 'border-border bg-surface text-text-primary hover:border-primary/40',
                      )}
                    >
                      <span className="font-semibold">{c.name}</span>
                      <span className="ml-1 font-mono text-2xs text-text-muted">{c.code}</span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-2xs text-text-muted">
                Select at least one permitted location. {locationIds.length} selected.
                {assetError && <span className="block text-danger">{assetError}</span>}
              </p>
            </FormField>

            <div className="rounded-xl border border-primary/30 bg-primary-soft/40 p-3 text-xs text-text-secondary">
              This wizard creates a paused campaign. Review the full campaign in the workspace before activating it.
            </div>
          </Card>
        )}

        {/* Step 3 — Creative */}
        {step === 3 && (
          <Card>
            <h3 className="mb-1 text-base font-bold text-text-primary">Ad creative</h3>
            <p className="mb-4 text-xs text-text-muted">TikTok ads must be videos — paste a public URL and we'll upload it.</p>

            <FormField label="Identity (the TikTok handle that posts the ad)" required>
              {identities.length === 0 ? (
                <div className="rounded-lg border border-dashed border-warning/50 bg-warning-soft/30 p-3 text-xs text-warning">
                  No TikTok identity yet. Link an account under <Link to="/tiktok/account" className="underline">TikTok → Account</Link> first.
                </div>
              ) : (
                <Select value={identityId} onChange={(e) => setIdentityId(e.target.value)}>
                  {identities.map((i) => (
                    <option key={i.identityId} value={i.identityId}>
                      {i.displayName ?? i.identityId} ({i.identityType})
                    </option>
                  ))}
                </Select>
              )}
            </FormField>

            <FormField label="Video" required>
              {videoId ? (
                <div className="flex items-center gap-3 rounded-lg border border-success/30 bg-success-soft/30 p-3">
                  <Video size={20} className="text-success shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-success">Video uploaded</div>
                    <div className="text-2xs text-text-muted truncate">{videoUrl || videoId}</div>
                  </div>
                  <button onClick={() => { setVideoId(''); setVideoUrl(''); setVideoUrlInput(''); }} className="text-2xs text-text-muted hover:text-danger">
                    Replace
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={videoUrlInput}
                      onChange={(e) => setVideoUrlInput(e.target.value)}
                      placeholder="https://yourcdn.com/your-vertical-video.mp4"
                      disabled={uploadingVideo}
                    />
                    <Button variant="secondary" onClick={() => setLibraryOpen(true)} disabled={uploadingVideo} iconLeft={<Images size={13} />}>
                      Library
                    </Button>
                    <Button onClick={onUploadVideo} loading={uploadingVideo} disabled={!videoUrlInput.trim()}>
                      {uploadingVideo ? 'Uploading…' : 'Upload'}
                    </Button>
                  </div>
                  <p className="text-2xs text-text-muted">9:16 vertical, ≤ 60s, MP4 recommended. TikTok pulls the file from the URL — make sure it's public.</p>
                </div>
              )}
            </FormField>

            {/* AI enhance bar */}
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary-soft/40 p-2.5">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-white">
                <Wand2 size={12} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-text-primary">Enhance with AI</div>
                <div className="text-2xs text-text-muted">
                  Type a rough draft caption below, then click Enhance. Uses your default model from <a className="underline" href="/settings/ai">Settings → AI</a>.
                </div>
              </div>
              <Button onClick={enhance} loading={enhancing}>
                <Wand2 size={12} /> Enhance
              </Button>
            </div>

            <FormField label="Caption" required>
              <Textarea
                rows={3}
                value={adText}
                onChange={(e) => setAdText(e.target.value)}
                placeholder="What viewers see under the video. Keep it punchy — under 100 chars works best."
                maxLength={300}
              />
              <p className="mt-1 text-2xs text-text-muted">{adText.length} / 300</p>
            </FormField>

            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label="Call to action">
                <Select value={callToAction} onChange={(e) => setCallToAction(e.target.value)}>
                  {CTA_OPTIONS.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </Select>
              </FormField>
              {goal !== 'app_installs' && goal !== 'lead_generation' && <FormField label="Landing page URL" required>
                <Input
                  value={landingPageUrl}
                  onChange={(e) => setLandingPageUrl(e.target.value)}
                  placeholder="https://yoursite.com/sale"
                  required
                />
              </FormField>}
            </div>
            {assetError && <p className="mt-3 text-xs text-danger">{assetError}</p>}
            {goal === 'app_installs' && <FormField label="App to promote" required hint="Clicks to the store only. Install attribution is not configured.">
              <Select value={appId} disabled={!!registryAppUid} onChange={event => setAppId(event.target.value)}>
                <option value="">Select a registered app</option>
                {apps.map(app => <option key={app.appId} value={app.appId} disabled={app.platform === 'UNKNOWN'}>{app.appName} ({app.platform})</option>)}
              </Select>
              {!apps.length && <p className="mt-1 text-xs text-warning">Register the app in TikTok Ads Manager, then reload.</p>}
            </FormField>}
            {goal === 'lead_generation' && <FormField label="Instant Form" required>
              <Select value={pageId} onChange={event => setPageId(event.target.value)}>
                <option value="">Select an Instant Form</option>
                {forms.map(form => <option key={form.pageId} value={form.pageId}>{form.title}</option>)}
              </Select>
              {!forms.length && <p className="mt-1 text-xs text-warning">Create an Instant Form in TikTok Ads Manager, then reload.</p>}
            </FormField>}
            {goal === 'conversions' && <div className="grid gap-3 sm:grid-cols-2">
              <FormField label="Pixel" required><Select value={pixelId} onChange={event => { setPixelId(event.target.value); setOptimizationEvent(''); }}>
                <option value="">Select a pixel</option>
                {pixels.map(pixel => <option key={pixel.pixelId} value={pixel.pixelId}>{pixel.pixelName}</option>)}
              </Select></FormField>
              <FormField label="Conversion event" required><Select value={optimizationEvent} onChange={event => setOptimizationEvent(event.target.value)}>
                <option value="">Select a configured event</option>
                {pixels.find(pixel => pixel.pixelId === pixelId)?.events.map(event => <option key={event.eventType} value={event.eventType}>{event.label ?? event.eventType}</option>)}
              </Select></FormField>
              <p className="text-xs text-warning sm:col-span-2">Conversion ads also depend on advertiser approval and pixel access. TikTok may restrict this objective.</p>
            </div>}
          </Card>
        )}

        {/* Step 4 — Review */}
        {step === 4 && goalMeta && (
          <Card>
            <h3 className="mb-1 text-base font-bold text-text-primary">Review and launch</h3>
            <p className="mb-4 text-xs text-text-muted">One click creates the campaign, ad group, and ad together.</p>

            <div className="space-y-3">
              <ReviewRow icon={<Sparkles size={14} />} label="Goal" value={goalMeta.title} />
              <ReviewRow icon={<Music2 size={14} />} label="Name" value={name} />
              <ReviewRow
                icon={<CircleDollarSign size={14} />}
                label="Budget"
                value={`${dailyBudget}/day · ${endsOnDate && endDate ? `ends ${endDate}` : 'runs continuously'}`}
              />
              <ReviewRow
                icon={<Globe2 size={14} />}
                label="Countries"
                value={locationIds
                  .map((id) => countries.find((c) => c.id === id)?.name ?? id)
                  .join(', ')}
              />
              <ReviewRow
                icon={<Zap size={14} />}
                label="Start"
                value={launchActive ? 'Active immediately' : 'Paused — flip on after review'}
              />
              <ReviewRow icon={<Video size={14} />} label="Video" value={videoUrl || videoId} />
              <ReviewRow icon={<MessageCircle size={14} />} label="Caption" value={adText} />
              <ReviewRow icon={<MousePointerClick size={14} />} label="Call to action" value={callToAction.replace(/_/g, ' ')} />
              {landingPageUrl && <ReviewRow icon={<Globe2 size={14} />} label="Landing page" value={landingPageUrl} />}
            </div>

            <div className="mt-4 rounded-lg border border-border bg-surface-alt/40 p-3">
              <p className="text-2xs text-text-muted">
                <strong>Behind the scenes:</strong> we map your goal to TikTok's terminology — objective_type,
                optimization_goal, billing_event, and bid_type. You don't need to know any of those.
              </p>
            </div>
          </Card>
        )}

        {/* Navigation */}
        <div className="mt-5 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={back}
            disabled={step === 1 || submitting}
            iconLeft={<ChevronLeft size={14} />}
          >
            Back
          </Button>

          {step < 4 ? (
            <Button onClick={next} disabled={!canAdvance[step]} iconRight={<ChevronRight size={14} />}>
              Next
            </Button>
          ) : (
            <Button onClick={onLaunch} loading={submitting} disabled={submitting}>
              {launchActive ? 'Launch campaign' : 'Create paused campaign'}
            </Button>
          )}
        </div>
      </div>

      <MediaLibraryPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        mediaType="video"
        onPick={(asset) => setVideoUrlInput(asset.url)}
      />
    </>
  );
}

function ReviewRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 border-b border-border pb-2.5 last:border-0 last:pb-0">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-2xs uppercase tracking-wider text-text-muted">{label}</div>
        <div className="text-sm font-medium text-text-primary break-words">{value}</div>
      </div>
    </div>
  );
}
